import { COLORS } from './chartColors';
import { StatusFilter } from '../../hooks/useChartData';
import { sortLabelsChronologically } from './helpers/dateHelpers';
import { parseDataKey } from './helpers/dataHelpers';
import { groupByPeriodAndKey } from './helpers/dataGroupingHelpers';
import { SelectedEntity } from './helpers/types';

// Utility function to convert snake_case to title case
const formatStateName = (state: string): string => {
  return state
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const applicationCountStatusTransformer = (
  data: Record<string, { data: any[] }>,
  selectedEntities: SelectedEntity[],
  selectedStatuses: StatusFilter[] = []
) => {
  // Flatten all records and attach period label from key
  const allRecords: (any & { _periodLabel: string })[] = [];
  Object.entries(data).forEach(([key, value]) => {
    const { monthLabel, shouldInclude } = parseDataKey(key, selectedEntities);
    if (!shouldInclude) return;
    if (Array.isArray(value.data)) {
      value.data.forEach((record) => {
        allRecords.push({ ...record, _periodLabel: monthLabel });
      });
    }
  });

  // Filter by selected statuses if any are selected
  const isAllStatesSelected = selectedStatuses?.some(
    (stateFilter: StatusFilter) => stateFilter.name === 'All'
  );
  const filteredRecords =
    selectedStatuses && selectedStatuses.length > 0 && !isAllStatesSelected
      ? allRecords.filter((record) =>
          selectedStatuses.some(
            (stateFilter: StatusFilter) =>
              stateFilter.name === formatStateName(record.status || 'unknown')
          )
        )
      : allRecords;

  // Group by period label and status
  const monthlyStatusCounts = groupByPeriodAndKey(
    filteredRecords,
    (record) => record._periodLabel,
    (record) => record.status || 'unknown'
  );

  const allStatuses = new Set<string>(filteredRecords.map((r) => r.status || 'unknown'));
  const sortedLabels = sortLabelsChronologically(Object.keys(monthlyStatusCounts));

  const datasets = Array.from(allStatuses).map((status, index) => ({
    label: formatStateName(status),
    data: sortedLabels.map((label) => monthlyStatusCounts[label][status] || 0),
    backgroundColor: COLORS[index % COLORS.length],
    borderWidth: 1,
    stack: 'applicationStatus'
  }));

  return {
    labels: sortedLabels,
    datasets
  };
};
